import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { onboardingService } from '@/services/apiClient';
import type {
  OnboardingListQuery,
  UpdateStatusRequest,
} from '@/types/onboarding';

/**
 * Onboarding Hooks
 * React Query hooks for /api/v1/onboarding/* endpoints
 */

// Query keys
export const onboardingKeys = {
  all: ['onboarding'] as const,
  dashboard: () => [...onboardingKeys.all, 'dashboard'] as const,
  list: (query: OnboardingListQuery) => [...onboardingKeys.all, 'list', query] as const,
  processFlow: () => [...onboardingKeys.all, 'processFlow'] as const,
  activityLog: (userId: string) => [...onboardingKeys.all, 'activity', userId] as const,
};

/**
 * Hook to fetch onboarding dashboard metrics
 */
export const useOnboardingDashboard = () => {
  return useQuery({
    queryKey: onboardingKeys.dashboard(),
    queryFn: () => onboardingService.getDashboard(),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to fetch onboarding list with filtering
 */
export const useOnboardingList = (query: OnboardingListQuery = {}) => {
  return useQuery({
    queryKey: onboardingKeys.list(query),
    queryFn: () => onboardingService.getList(query),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to fetch process flow
 */
export const useProcessFlow = () => {
  return useQuery({
    queryKey: onboardingKeys.processFlow(),
    queryFn: () => onboardingService.getProcessFlow(),
    staleTime: 10 * 60 * 1000, // 10 minutes (process flow doesn't change often)
  });
};

/**
 * Hook to fetch activity log for a user
 */
export const useActivityLog = (userId: string | undefined, enabled = true) => {
  return useQuery({
    queryKey: onboardingKeys.activityLog(userId || ''),
    queryFn: () => onboardingService.getActivityLog(userId || ''),
    enabled: enabled && !!userId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

/**
 * Hook to update onboarding status
 */
export const useUpdateOnboardingStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UpdateStatusRequest }) =>
      onboardingService.updateStatus(userId, data),
    onSuccess: () => {
      // Invalidate all onboarding queries to refetch data
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
    },
  });
};

